import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useRequestReimbursement } from '../api/reimbursementApi';
import { ReimbursementReasonFields } from '../components/ReimbursementReasonFields';
import { ReimbursementStepActions } from '../components/ReimbursementStepActions';
import { reimbursementReasonLabelKey } from '../labels';
import { useReimbursementDraft } from '../state/reimbursementDraftContext';
import { toReimbursementRequest } from '../utils/toReimbursementRequest';

export function DetailsStep(): ReactElement {
    const { t } = useTranslation();
    const { draft, updateDraft, offerRequestLeadUuid } = useReimbursementDraft();
    const requestReimbursement = useRequestReimbursement(offerRequestLeadUuid);
    const request = useMemo(() => toReimbursementRequest(draft), [draft]);

    if (draft.reason === undefined) {
        return <Alert severity="info">{t('REIMBURSEMENT.DETAILS.PICK_REASON_FIRST')}</Alert>;
    }

    if (draft.submitted) {
        return (
            <Alert severity="success" variant="outlined">
                {t('REIMBURSEMENT.DETAILS.SUBMITTED')}
            </Alert>
        );
    }

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h2">
                    {t('REIMBURSEMENT.DETAILS.TITLE')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t(reimbursementReasonLabelKey(draft.reason))}
                </Typography>
            </Stack>

            <ReimbursementReasonFields reason={draft.reason} />

            {requestReimbursement.isError && <Alert severity="error">{t('REIMBURSEMENT.DETAILS.SUBMIT_ERROR')}</Alert>}

            <ReimbursementStepActions
                currentStep="details"
                nextDisabled={request === undefined || requestReimbursement.isPending}
                onNext={() => {
                    if (request !== undefined) {
                        requestReimbursement.mutate(request, { onSuccess: () => updateDraft({ submitted: true }) });
                    }
                }}
            />
        </Stack>
    );
}
